<template>
  <modal-fullscreen
    :visible="visible"
    :title="title"
    width="1000px"
    wrap-class-name="signed-document-modal"
    :destroy-on-close="true"
    @cancel="closeModal"
  >
    <a-modal v-model="visibleCommentModal" title="Reason for decline">
      <a-input placeholder="Explain what needs to be fixed." v-model="comment" />
      <p v-if="!comment" class="validation-copy">Add a reason before declining this step.</p>

      <template slot="footer">
        <div class="flex justify-content-between">
          <a-button @click="visibleCommentModal = false"> Cancel </a-button>
          <a-button type="danger" @click="declineStep" :disabled="!comment"> Decline Step </a-button>
        </div>
      </template>
    </a-modal>

    <div class="flex flex-column gap-3" v-if="step">
      <div class="flex justify-content-between align-items-start">
        <span class="flex gap-2 align-items-center">
          <a-badge :number-style="{ backgroundColor: '#3b82f6' }" :count="step?.number" />{{
            step.body.name ?? step.body.template.name
          }}</span
        >
        <div class="flex flex-column gap-2">
          <div class="flex gap-2 align-items-center" :style="{ color: '#10b981' }">
            <a-icon
              width="20px"
              height="20px"
              :style="{ color: SEQUENCE_STEP_STATUS[step.status]?.color }"
              :icon="SEQUENCE_STEP_STATUS[step.status]?.icon"
            />
            {{ SEQUENCE_STEP_STATUS[step.status]?.title }}
          </div>
          <template v-if="step.review?.status">
            <div
              class="flex gap-2 align-items-center"
              :style="{
                color: step.review?.status === 'approved' ? '#10b981' : '#ef4444'
              }"
            >
              <a-icon
                :icon="step.review?.status === 'approved' ? 'mdi:check-circle-outline' : 'mdi:close-circle-outline'"
                width="20px"
                height="20px"
                :style="{
                  color: step.review?.status === 'approved' ? '#10b981' : '#ef4444'
                }"
              />

              {{ capitalize(step.review?.status) }}
            </div>
            <span v-if="step?.review?.comment">Reason: {{ step?.review?.comment }}</span>
          </template>
        </div>
      </div>

      <template v-if="step.type === 'sign_document'">
        <h4 class="m-0">{{ step.body.name ?? step.body.template.name }}</h4>
        <template v-if="step.body.type === 'pdf'">
          <pdf-viewer :file="step.body.body.file.url" :continuous="true" :fullscreen-enabled="false" />
        </template>
        <template v-else-if="step.body.type === 'text'">
          <p class="m-0" v-html="safeDocumentBodyItems(step.body.body.items)"></p>
        </template>
        <template v-else-if="step.body.type === 'video'">
          <video
            v-if="step.body.body.file.url"
            class="ant-col-24 mt-5"
            :style="{ height: '336px' }"
            :src="step.body.body.file.url"
            controls
          ></video>
          <p v-if="step.body.body.details" class="m-0">Details: {{ step.body.body.details }}</p>
        </template>
        <template v-else-if="step.body.type === 'survey'">
          <p v-if="step.body.body.survey.details" class="m-0">Details: {{ step.body.body.survey.details }}</p>
          <a-progress
            :status="
              countCompletionRate(step.body.body.survey.associate_result.completion_rate) >=
              step.body.body.survey.completion_rate
                ? 'success'
                : 'exception'
            "
            :percent="countCompletionRate(step.body.body.survey.associate_result.completion_rate)"
          />
          <div class="questions-list">
            <div v-for="(question, idx) in step.body.body.survey.questions" :key="question.id">
              <div class="flex gap-2 mb-1">
                <a-badge :number-style="{ backgroundColor: '#3b82f6' }" :count="idx + 1" :overflowCount="1000" />

                {{ question.name }}
              </div>

              <div class="answers-list">
                <div
                  class="answer"
                  :style="{
                    borderColor: answer.correct ? '#10b981' : '#ef4444'
                  }"
                  v-for="answer in question.answers"
                  :key="answer.id"
                >
                  <a-checkbox :checked="checkedAnswer(answer, step.body.body.survey.associate_result.answers)" />

                  <answer-correctness-icon :correct="answer.correct" />
                  {{ answer.name }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <div>
          <p>Associate Signature</p>
          <associate-signature
            v-if="step.body.signature"
            key="associate_signature"
            :available-signature="step.body.signature"
          />
          <a-empty v-else description="No signature on file." />
        </div>
      </template>
      <template v-else>
        <div class="grid">
          <template v-if="step.body.template.can_expire">
            <b class="col-4">Expiration Date</b>
            <span class="col-8">{{
              step.body.document?.expiration_date ? formatDateString(step.body.document?.expiration_date) : '-'
            }}</span></template
          >
          <template v-if="step.body.template.has_number">
            <b class="col-4">Document Number</b>
            <span class="col-8">{{ step.body.document?.number ? step.body.document?.number : '-' }}</span></template
          >
        </div>
        <document-attachments :files="step.body.document?.files ?? []" :is-view="true" :with-title="false" />
      </template>
    </div>
    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal">Close</a-button>
        <div class="flex justify-content-end" v-if="showActions">
          <a-button type="danger" @click="visibleCommentModal = true">Decline</a-button>
          <a-button type="primary" @click="approveStep">Approve</a-button>
        </div>
      </div>
    </template>
  </modal-fullscreen>
</template>

<script>
import ModalFullscreen from '@/components/entities/modal/modal-fullscreen.vue'
import PdfViewer from '@/components/entities/viewer/pdf-viewer.vue'
import Signature from '@/components/signature.vue'
import ShiftFormAttachments from '@/components/form-attachments.vue'
import { SEQUENCE_STEP_STATUS } from '@/store/modules/sequence/constants'
import util from '@/util'
import { countCompletionRate } from '@/utils/survey'
import { Badge, Button, Checkbox, Empty, Input, Modal, Progress } from 'ant-design-vue'
import moment from 'moment-timezone'
import { joinHtmlItems } from '@/utils/safe-html'
import AnswerCorrectnessIcon from '@/modules/document/components/answer-correctness-icon.vue'

export default {
  components: {
    'a-button': Button,
    'a-modal': Modal,
    'a-input': Input,
    'a-badge': Badge,
    'a-checkbox': Checkbox,
    'a-empty': Empty,
    'a-progress': Progress,
    'associate-signature': Signature,
    'document-attachments': ShiftFormAttachments,
    'modal-fullscreen': ModalFullscreen,
    'pdf-viewer': PdfViewer,
    'answer-correctness-icon': AnswerCorrectnessIcon
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    step: {
      type: Object,
      default: undefined
    },
    title: {
      type: String,
      default: 'Step evidence'
    },
    showActions: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      comment: '',

      visibleCommentModal: false,

      SEQUENCE_STEP_STATUS
    }
  },
  watch: {
    visibleCommentModal: {
      handler() {
        this.comment = ''
      }
    }
  },
  methods: {
    capitalize: util.capitalize,
    safeDocumentBodyItems(items) {
      return joinHtmlItems(items)
    },
    closeModal() {
      this.$emit('update:visible', false)
    },
    formatDateString(dateString) {
      return moment(dateString).utc().format('MM/DD/YYYY')
    },
    declineStep() {
      this.step.review = {
        comment: this.comment,
        status: 'declined'
      }
      this.visibleCommentModal = false
      this.closeModal()
    },
    approveStep() {
      this.step.review = {
        comment: '',
        status: 'approved'
      }
      this.closeModal()
    },
    checkedAnswer(answer, result) {
      return result.some(item => item.id === answer.id)
    },
    countCompletionRate
  }
}
</script>

<style scoped>
::v-deep .signed-document-modal .ant-modal-body {
  max-height: calc(100vh - 180px);
  overflow-y: auto;
}

::v-deep .signed-document-modal.modal-fullscreen-wrap--expanded .ant-modal-body {
  max-height: none;
}

.questions-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 300px;
  overflow: auto;
  border: 1px solid var(--border-color-base);
  border-radius: 10px;
  padding: 16px;
}

.answers-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.answer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 1px solid var(--border-color-base);
  border-radius: 10px;
}

.validation-copy {
  color: #ef4444;
  margin: 8px 0 0;
}
</style>
